import { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../common/Button';
import './AddressEditor.css';

const EMPTY_FORM = {
  address_type: 'shipping',
  street_address: '',
  street_address_2: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'USA',
  notes: '',
  is_default: false,
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

// ── Shared form utilities ───────────────────────────────────────────────
const validateAddress = (form) => {
  const e = {};
  if (!form.street_address.trim()) e.street_address = 'Street address is required.';
  if (!form.city.trim()) e.city = 'City is required.';
  if (!form.state.trim()) e.state = 'State is required.';
  if (!form.zip_code.trim()) e.zip_code = 'ZIP code is required.';
  return e;
};

const makeChangeHandler = (setForm, setErrors) => (e) => {
  const { name, value, type, checked } = e.target;
  setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  setErrors((prev) => prev[name] ? { ...prev, [name]: '' } : prev);
};

/**
 * AddressEditor — inline address management for the customer detail view.
 * Handles add, edit, set-default, and delete for both billing and shipping.
 */
const AddressEditor = ({
  companyId,
  addresses,
  onAdd,
  onEdit,
  onDelete,
  onSetDefault,
  isSaving,
}) => {
  const billing = addresses.filter((a) => a.address_type === 'billing');
  const shipping = addresses.filter((a) => a.address_type === 'shipping');

  return (
    <div className="addr-editor">

      {/* ── Billing section ──────────────────────────────── */}
      <AddressSection
        title="Billing"
        addressType="billing"
        addresses={billing}
        companyId={companyId}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        onSetDefault={onSetDefault}
        isSaving={isSaving}
      />

      {/* ── Shipping section ─────────────────────────────── */}
      <AddressSection
        title="Shipping"
        addressType="shipping"
        addresses={shipping}
        companyId={companyId}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        onSetDefault={onSetDefault}
        isSaving={isSaving}
      />

    </div>
  );
};

// ── Sub-component: one type section (billing or shipping) ──────────────
const AddressSection = ({ title, addressType, addresses, companyId, onAdd, onEdit, onDelete, onSetDefault, isSaving }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [editError, setEditError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(null);
  const [addErrors, setAddErrors] = useState({});
  const [addError, setAddError] = useState(null);

  const handleEditChange = makeChangeHandler(setEditForm, setEditErrors);
  const handleAddChange = makeChangeHandler(setAddForm, setAddErrors);

  const openAdd = () => {
    setAddForm({ ...EMPTY_FORM, address_type: addressType });
    setAddErrors({});
    setAddError(null);
    setShowAddForm(true);
    setEditingId(null);
  };

  const closeAdd = () => {
    setShowAddForm(false);
    setAddForm(null);
    setAddErrors({});
    setAddError(null);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const errs = validateAddress(addForm);
    if (Object.keys(errs).length) { setAddErrors(errs); return; }
    try {
      await onAdd({ ...addForm, company: companyId });
      closeAdd();
    } catch (err) {
      setAddError(err?.message ?? 'Failed to add address. Please try again.');
    }
  };

  const openEdit = (addr) => {
    setEditForm({
      address_type: addr.address_type,
      street_address: addr.street_address,
      street_address_2: addr.street_address_2 || '',
      city: addr.city,
      state: addr.state,
      zip_code: addr.zip_code,
      country: addr.country || 'USA',
      notes: addr.notes || '',
      is_default: addr.is_default,
    });
    setEditErrors({});
    setEditError(null);
    setEditingId(addr.id);
    setShowAddForm(false);
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditForm(null);
    setEditErrors({});
    setEditError(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const errs = validateAddress(editForm);
    if (Object.keys(errs).length) { setEditErrors(errs); return; }
    try {
      await onEdit(editingId, editForm);
      closeEdit();
    } catch (err) {
      setEditError(err?.message ?? 'Failed to save address. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    setDeleteError(null);
    try {
      await onDelete(id);
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err?.message ?? 'Failed to delete address. Please try again.');
    }
  };

  return (
    <div className="addr-editor__section">
      <h4 className="addr-editor__type">{title}</h4>

      {addresses.length === 0 && !showAddForm && (
        <p className="addr-editor__empty">No {title.toLowerCase()} addresses.</p>
      )}

      {addresses.map((addr) => (
        <div key={addr.id} className={`addr-editor__block${addr.is_default ? ' addr-editor__block--default' : ''}`}>
          {editingId === addr.id ? (
            /* ── Inline edit form ── */
            <form className="addr-editor__form" onSubmit={handleEditSubmit} noValidate>
              <AddressFormFields form={editForm} errors={editErrors} onChange={handleEditChange} hideType />
              {editError && <p className="addr-editor__error addr-editor__error--form">{editError}</p>}
              <div className="addr-editor__form-actions">
                <Button type="button" variant="secondary" onClick={closeEdit} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          ) : confirmDelete === addr.id ? (
            /* ── Delete confirmation ── */
            <div className="addr-editor__confirm">
              <span className="addr-editor__confirm-text">Delete this address?</span>
              {deleteError && <p className="addr-editor__error addr-editor__error--form">{deleteError}</p>}
              <div className="addr-editor__confirm-actions">
                <Button type="button" variant="secondary" onClick={() => { setConfirmDelete(null); setDeleteError(null); }} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="button" variant="danger" onClick={() => handleDelete(addr.id)} disabled={isSaving}>
                  {isSaving ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          ) : (
            /* ── Read view ── */
            <>
              {addr.is_default && (
                <span className="addr-editor__default-badge">Default</span>
              )}
              <div className="addr-editor__lines">
                <div className="addr-editor__line">{addr.street_address}</div>
                {addr.street_address_2 && (
                  <div className="addr-editor__line">{addr.street_address_2}</div>
                )}
                <div className="addr-editor__line">
                  {addr.city}, {addr.state} {addr.zip_code}
                </div>
                {addr.country && addr.country !== 'USA' && (
                  <div className="addr-editor__line">{addr.country}</div>
                )}
                {addr.notes && (
                  <div className="addr-editor__line addr-editor__line--notes">{addr.notes}</div>
                )}
              </div>
              <div className="addr-editor__row-actions">
                {!addr.is_default && (
                  <button
                    type="button"
                    className="addr-editor__action addr-editor__action--default"
                    onClick={() => onSetDefault(addr.id)}
                    disabled={isSaving}
                  >
                    Set default
                  </button>
                )}
                <button
                  type="button"
                  className="addr-editor__action addr-editor__action--edit"
                  onClick={() => openEdit(addr)}
                  disabled={isSaving || editingId !== null}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="addr-editor__action addr-editor__action--delete"
                  onClick={() => setConfirmDelete(addr.id)}
                  disabled={isSaving || editingId !== null}
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      ))}

      {/* ── Add form (inline, per section) ── */}
      {showAddForm ? (
        <form className="addr-editor__form addr-editor__form--new" onSubmit={handleAddSubmit} noValidate>
          <AddressFormFields form={addForm} errors={addErrors} onChange={handleAddChange} hideType />
          {addError && <p className="addr-editor__error addr-editor__error--form">{addError}</p>}
          <div className="addr-editor__form-actions">
            <Button type="button" variant="secondary" onClick={closeAdd} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Add Address'}
            </Button>
          </div>
        </form>
      ) : (
        !editingId && (
          <button type="button" className="addr-editor__add-btn" onClick={openAdd}>
            <span className="addr-editor__add-icon" aria-hidden="true">+</span>
            Add {title} Address
          </button>
        )
      )}
    </div>
  );
};

// ── Sub-component: shared form fields ──────────────────────────────────
const AddressFormFields = ({ form, errors, onChange, hideType }) => (
  <div className="addr-editor__fields">
    {!hideType && (
      <div className="addr-editor__field-row">
        <label className="addr-editor__label">Type</label>
        <select
          name="address_type"
          className="addr-editor__select"
          value={form.address_type}
          onChange={onChange}
        >
          <option value="billing">Billing</option>
          <option value="shipping">Shipping</option>
        </select>
      </div>
    )}

    <div className="addr-editor__field-row">
      <label className="addr-editor__label">Street Address *</label>
      <input
        name="street_address"
        className={`addr-editor__input${errors.street_address ? ' addr-editor__input--error' : ''}`}
        value={form.street_address}
        onChange={onChange}
        placeholder="123 Main St"
        maxLength={255}
      />
      {errors.street_address && <span className="addr-editor__error">{errors.street_address}</span>}
    </div>

    <div className="addr-editor__field-row">
      <label className="addr-editor__label">Suite / Unit</label>
      <input
        name="street_address_2"
        className="addr-editor__input"
        value={form.street_address_2}
        onChange={onChange}
        placeholder="Suite 100"
        maxLength={255}
      />
    </div>

    <div className="addr-editor__field-group">
      <div className="addr-editor__field-row addr-editor__field-row--grow">
        <label className="addr-editor__label">City *</label>
        <input
          name="city"
          className={`addr-editor__input${errors.city ? ' addr-editor__input--error' : ''}`}
          value={form.city}
          onChange={onChange}
          placeholder="Nashville"
          maxLength={100}
        />
        {errors.city && <span className="addr-editor__error">{errors.city}</span>}
      </div>

      <div className="addr-editor__field-row addr-editor__field-row--state">
        <label className="addr-editor__label">State *</label>
        <select
          name="state"
          className={`addr-editor__select${errors.state ? ' addr-editor__input--error' : ''}`}
          value={form.state}
          onChange={onChange}
        >
          <option value="">—</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {errors.state && <span className="addr-editor__error">{errors.state}</span>}
      </div>

      <div className="addr-editor__field-row addr-editor__field-row--zip">
        <label className="addr-editor__label">ZIP *</label>
        <input
          name="zip_code"
          className={`addr-editor__input${errors.zip_code ? ' addr-editor__input--error' : ''}`}
          value={form.zip_code}
          onChange={onChange}
          placeholder="37210"
          maxLength={20}
        />
        {errors.zip_code && <span className="addr-editor__error">{errors.zip_code}</span>}
      </div>
    </div>

    <div className="addr-editor__field-row">
      <label className="addr-editor__label">Delivery Notes</label>
      <textarea
        name="notes"
        className="addr-editor__textarea"
        value={form.notes}
        onChange={onChange}
        placeholder="e.g. Ring bell, leave at back door"
        rows={2}
      />
    </div>

    <div className="addr-editor__field-row addr-editor__field-row--checkbox">
      <label className="addr-editor__checkbox-label">
        <input
          type="checkbox"
          name="is_default"
          checked={form.is_default}
          onChange={onChange}
          className="addr-editor__checkbox"
        />
        Set as default for this type
      </label>
    </div>
  </div>
);

AddressEditor.propTypes = {
  companyId: PropTypes.number.isRequired,
  addresses: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

AddressSection.propTypes = {
  title: PropTypes.string.isRequired,
  addressType: PropTypes.string.isRequired,
  addresses: PropTypes.array.isRequired,
  companyId: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

AddressFormFields.propTypes = {
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  hideType: PropTypes.bool,
};

export default AddressEditor;
